/**
 * Scrollt der Leser gerade? — EINE Regel für alle, die `scrollTop` schreiben. (02.10.2026, pre.3607)
 *
 * Martin, iPhone, 02.10. ~19:40: *„Kümmere dich jetzt endlich darum, dass das Scrollen am iPhone sauber
 * funktioniert und nicht hin und her zuckelt."*
 *
 * ## Was gemessen wurde (echtes WebKit, tests/browser/scroll-iphone-webkit.mts)
 *
 * Jeder programmatische Schreibzugriff auf `scrollTop` bekam einen Zähler mit Herkunft. Dann wischte der
 * Leser wie am iPhone: Finger, Loslassen, Nachlaufen ohne Eingabe, Ausfedern am Rand. Auf main:
 *
 *   - Antwort streamt, er wischt zurück ins Ende und legt den Daumen ruhig auf: `chat-folgen.ts` schrieb
 *     zehnmal während Nachlauf, Ausfedern und aufliegendem Daumen (je +21/+42 Punkte — dieselbe Form wie
 *     `art=hoehe delta=+42` alle 0,3 s im Journal vom 02.10.). Die eigene Gestenwache sah nur `touchstart`,
 *     `touchmove` und das Rad, jeweils 250 ms lang: das Nachlaufen NACH dem Loslassen und ein ruhig
 *     liegender Daumen galten als „niemand da“.
 *   - Eine Antwort kommt von einem anderen Gerät, die vorige lange klappt über ihm zu: der Klapper hielt
 *     den Ort (`scroll-ort.ts`) — mitten im Wisch.
 *   - Schneller Wisch an den Anfang: das Ältere kam an, eingefügt und Ort gehalten — mitten im Ausfedern.
 *
 * Am iPhone bricht jedes dieser Schreiben das Nachlaufen ab oder versetzt es: das Zuckeln. Dass der Ort
 * danach stimmt (Bezugsblase 0–2 Punkte), hilft dagegen nicht.
 *
 * ## Die Regel
 *
 * Der Leser bewegt die Fläche, solange
 *   - ein Finger auf dem Glas liegt (von `touchstart` bis `touchend`/`touchcancel` — auch wenn er ruht),
 *   - oder seit dem letzten Scroll-Ereignis, das nicht vom Programm kam, keine `RUHE_MS` vergangen sind
 *     (Nachlaufen und Ausfedern melden ein Ereignis je Bild; dazu Rad, Tasten, Trackpad),
 *   - oder seit dem letzten Mausrad-Dreh keine `RUHE_MS` vergangen sind.
 *
 * ⚠️ pre.3612: „Ruhe“ ist mindestens doppelt so lang wie der letzte Abstand zweier Scroll-Ereignisse derselben
 *    Bewegung (`ruheFuer`, höchstens `RUHE_HOECHSTENS_MS`, nur Abstände bis `LUECKE_HOECHSTENS_MS`). Am 02.10., 22:14–22:16, kamen die Ereignisse beim
 *    Nachlaufen am iPhone im Abstand von 77–100 ms, einzelne Bilder dauerten bis 885 ms (Seite mit 45.000
 *    Knoten). Mit fest 150 ms galt eine solche Lücke als „still“ — und im echten WebKit unter Last schrieb das
 *    Nachladen dann zweimal mitten in den Wisch (Fall h2).
 *
 * Solange schreibt NIEMAND `scrollTop` aus einem Hintergrundereignis. Was warten muss, geht über
 * `nachRuhe` (holt nach, sobald es still ist; derselbe Schlüssel nur einmal, der letzte Auftrag gilt).
 * Unterschiede unter `MIN_SCHRITT_PX` werden gar nicht geschrieben (kein ±1-Zittern).
 *
 * ⚠️ Ein Tipp ist keine Bewegung: Nach `touchend` ohne Scroll-Ereignis gibt es nichts nachzulaufen. Darum
 *    gilt nach dem Loslassen nur noch die Ruhe nach dem letzten Scroll-Ereignis — der Knopf „nach unten“
 *    springt sofort.
 * ⚠️ Wird das Element unter dem Finger ersetzt, kommt `touchend` am Dokument nie an (der Knoten hängt
 *    nicht mehr im Baum). Darum hört die Wache zusätzlich am Ziel selbst — und als letzte Sicherung gilt
 *    ein Finger ohne jedes Berührungs-Ereignis nach `FINGER_HOECHSTENS_MS` als weg.
 * ⚠️ Eigenes Schreiben erzeugt ebenfalls ein Scroll-Ereignis. Steht die Fläche dort, wo `schreibeScrollTop`
 *    sie eben hingestellt hat, ist es keins des Lesers (sonst hielte das Nachziehen sich selbst an).
 * ⚠️ Hängt NICHT am Ruckel-Schalter „Scroll-Messfühler aus“: Das hier ist kein Messfühler, sondern die
 *    Regel, die den Leser schützt. Die Wachen sind passiv und kosten je Ereignis eine Zuweisung.
 */
/** So lange nach dem letzten fremden Scroll-Ereignis / Rad-Dreh (ms) gilt die Fläche noch als bewegt. */
export const RUHE_MS = 150;
/**
 * Größter Abstand zweier Scroll-Ereignisse (ms), der noch als DIESELBE Bewegung gilt. Am iPhone lagen sie beim
 * Nachlaufen unter Last bei 77–100 ms; was länger auseinander liegt, sind zwei Handlungen (ein Wisch, eine
 * Pause, ein Tipp) — sonst verlängerte jede Pause die Ruhe der nächsten.
 */
export const LUECKE_HOECHSTENS_MS = 300;
/** Längste Ruhe, die eine langsame Bewegung verlangen kann (ms): doppelter größter Abstand. */
export const RUHE_HOECHSTENS_MS = 2 * LUECKE_HOECHSTENS_MS;

/** Wie lange nach dem letzten Scroll-Ereignis es still sein muss, wenn die Ereignisse zuletzt `luecke` ms auseinander lagen. */
export function ruheFuer(luecke: number): number {
  return Math.min(RUHE_HOECHSTENS_MS, Math.max(RUHE_MS, 2 * luecke));
}

/** Ein Finger ohne jedes Berührungs-Ereignis gilt nach so vielen ms als weg (falls `touchend` nie ankommt). */
export const FINGER_HOECHSTENS_MS = 5000;
/**
 * So lange nach eigenem Schreiben (ms) gehört ein Scroll-Ereignis an genau dieser Stelle dem Programm.
 * 500 statt 150 (Prüfbefund 02.10.): Unter Last (Markdown, Bilder beim Streamen) kam das Echo später als
 * 150 ms und galt als Bewegung des Lesers — das Nachziehen hielt sich selbst an. Ein echtes Wischen bleibt
 * erkennbar, weil die Stelle dafür auf ±1 Punkt gleich sein müsste.
 */
export const EIGEN_MS = 500;
/** Auf so viele Punkte genau erkennt die Wache die eigene Stelle wieder (Rundung im Browser). */
const EIGEN_TOLERANZ_PX = 1;
/** Kleinere Unterschiede werden nicht geschrieben. */
export const MIN_SCHRITT_PX = 2;
/** So oft (ms) schaut ein wartender Auftrag nach, ob es still geworden ist. */
export const NACHSEHEN_MS = 60;

/** Was die Wache weiß — als reiner Zustand, damit die Regel ohne Browser prüfbar ist. */
interface RuheStand {
  /** Finger auf dem Glas. */
  finger: number;
  /** Letztes Berührungs-Ereignis (ms). */
  letzteBeruehrung: number;
  /** Letzter Rad-Dreh (ms). */
  letztesRad: number;
  /** Letztes fremdes Scroll-Ereignis irgendwo (ms). */
  letzterScroll: number;
  /** Abstand der letzten zwei fremden Scroll-Ereignisse irgendwo (ms; 0: keine laufende Bewegung). */
  luecke: number;
}

export function leererStand(): RuheStand {
  return { finger: 0, letzteBeruehrung: -Infinity, letztesRad: -Infinity, letzterScroll: -Infinity, luecke: 0 };
}

/** Der neue Abstand nach einem Scroll-Ereignis: nur innerhalb derselben Bewegung (sonst 0). Reine Rechnung. */
export function neueLuecke(vorher: number, jetzt: number): number {
  const d = jetzt - vorher;
  return d > 0 && d <= LUECKE_HOECHSTENS_MS ? d : 0;
}

/**
 * Die Regel, rein gerechnet. `letzterScrollHier` = letztes fremdes Scroll-Ereignis DIESER Fläche
 * (fehlt er, zählt jede Fläche). `fingerHier` = liegt der Finger auf dieser Fläche (siehe
 * `fingerGehoertZu`); ein Finger auf der Sprechtaste hält nicht den Chat oder die Mitschrift an.
 */
export function bewegtSich(s: RuheStand, jetzt: number, letzterScrollHier?: number, fingerHier = true, lueckeHier?: number): boolean {
  if (fingerHier && s.finger > 0 && jetzt - s.letzteBeruehrung < FINGER_HOECHSTENS_MS) return true;
  if (jetzt - s.letztesRad < RUHE_MS) return true;
  const zuletzt = letzterScrollHier ?? s.letzterScroll;
  return jetzt - zuletzt < ruheFuer(letzterScrollHier === undefined ? s.luecke : (lueckeHier ?? 0));
}

/**
 * Liegt der Finger auf `el`? Reine Rechnung (Prüfbefund 02.10.): Bis pre.3607 zählte jeder Finger für jede
 * Fläche — beim Halten der Sprechtaste lief deshalb die Live-Mitschrift nicht mehr mit, und der Chat zog
 * nicht nach, solange der Daumen auf dem Eingabefeld lag. Ohne Fläche, ohne bekanntes Ziel oder wenn das
 * Ziel nicht mehr im Baum hängt (die Blase unter dem Finger wurde ersetzt), zählt der Finger wie bisher.
 */
export function fingerGehoertZu(el: Element | null | undefined, ziel: Node | null): boolean {
  if (!el || !ziel?.isConnected) return true;
  return el.contains(ziel);
}

/** Ist dieses Scroll-Ereignis das Echo des eigenen Schreibens? Reine Rechnung. */
export function istEigenesEcho(mark: { oben: number; t: number } | undefined, oben: number, jetzt: number): boolean {
  return mark !== undefined && jetzt - mark.t >= 0 && jetzt - mark.t <= EIGEN_MS && Math.abs(oben - mark.oben) <= EIGEN_TOLERANZ_PX;
}

/* ------------------------------------------------------------------ *
 * Der Zustand im Browser
 * ------------------------------------------------------------------ */

const stand = leererStand();
let scrollJeFlaeche = new WeakMap<EventTarget, number>();
let lueckeJeFlaeche = new WeakMap<EventTarget, number>();
let eigen = new WeakMap<Element, { oben: number; t: number; wer: string; verbraucht?: boolean }>();
let gesehen = new WeakSet<Event>();
/** Wo der Finger zuletzt aufgesetzt hat (für `fingerGehoertZu`). */
let fingerZiel: Node | null = null;
let laeuft = false;

const uhr = (): number => (typeof performance !== "undefined" ? performance.now() : Date.now());

function fingerVon(ev: Event, sonst: number): number {
  const t = (ev as TouchEvent).touches;
  return t && typeof t.length === "number" ? t.length : sonst;
}

function beiLoslassen(ev: Event): void {
  if (gesehen.has(ev)) return;
  gesehen.add(ev);
  stand.finger = fingerVon(ev, Math.max(0, stand.finger - 1));
  stand.letzteBeruehrung = uhr();
}

/** Die Wachen einhängen (einmal; geschieht schon beim Laden des Moduls). */
export function starteScrollRuhe(): void {
  if (laeuft || typeof document === "undefined") return;
  laeuft = true;
  const fang = { capture: true, passive: true } as const;
  document.addEventListener(
    "touchstart",
    (ev) => {
      stand.finger = fingerVon(ev, stand.finger + 1);
      stand.letzteBeruehrung = uhr();
      // Nur ein echtes Element als Ziel; sonst (Dokument, unbekannt) zählt der Finger wie bisher überall.
      fingerZiel = ev.target instanceof Element ? ev.target : null;
      // Wird das Ziel gleich ersetzt, erreicht sein `touchend` das Dokument nicht mehr — am Ziel schon.
      const ziel = ev.target;
      if (ziel && ziel !== document) {
        ziel.addEventListener("touchend", beiLoslassen, { once: true, passive: true });
        ziel.addEventListener("touchcancel", beiLoslassen, { once: true, passive: true });
      }
    },
    fang,
  );
  document.addEventListener(
    "touchmove",
    () => {
      stand.letzteBeruehrung = uhr();
    },
    fang,
  );
  document.addEventListener("touchend", beiLoslassen, fang);
  document.addEventListener("touchcancel", beiLoslassen, fang);
  document.addEventListener(
    "wheel",
    () => {
      stand.letztesRad = uhr();
    },
    fang,
  );
  document.addEventListener(
    "scroll",
    (ev) => {
      const ziel = ev.target instanceof Element ? ev.target : (document.scrollingElement ?? document.documentElement);
      if (!ziel) return;
      const jetzt = uhr();
      /*
       * pre.3612: Ein Schreiben hat EIN Echo. Bis dahin galt jedes Ereignis an derselben Stelle binnen
       * EIGEN_MS als eigenes — auch das Ausfedern des Lesers, das genau am Ende zur Ruhe kommt, wo das
       * Nachziehen eben hingeschrieben hatte. Das Nachziehen schrieb dann in dieses Ausfedern hinein.
       */
      const mark = eigen.get(ziel);
      if (mark && !mark.verbraucht && istEigenesEcho(mark, ziel.scrollTop, jetzt)) {
        mark.verbraucht = true;
        return;
      }
      lueckeJeFlaeche.set(ziel, neueLuecke(scrollJeFlaeche.get(ziel) ?? -Infinity, jetzt));
      scrollJeFlaeche.set(ziel, jetzt);
      stand.luecke = neueLuecke(stand.letzterScroll, jetzt);
      stand.letzterScroll = jetzt;
      markiereScrollen();
    },
    fang,
  );
}

/* ------------------------------------------------------------------ *
 * Merkmal am Dokument: „es wird gescrollt“ (04.10.2026)
 * ------------------------------------------------------------------ */

/**
 * Steht (am Elternteil der Szene, s. `setzeMerkmal`), solange der Leser IRGENDEINE Fläche bewegt (dieselbe Regel wie `nutzerScrolltGerade()`
 * ohne Fläche) — gesetzt beim ersten fremden Scroll-Ereignis, nicht schon bei der Berührung (ein Tipp ist keine
 * Bewegung). Das Blatt hält damit Daueranimationen an, die sonst jedes Bild neu zeichnen ließen.
 *
 * Martin, iPhone, 04.10. 00:16: *„Das Scrollen geht noch immer nicht richtig!!!!“* — Zuhause, nachts:
 * `[scroll-t] ziel=view-zuhause median=83/103 ms` (≈ 10 Bilder je Sekunde, `schreiber=-`). Nachts laufen in der
 * Szene ~250 Sterne mit eigener Funkel-Animation, dazu Bärli, Mondschein, Glitzern — 272 Animationen, alle in
 * SVG. WebKit legt SVG-Teile nicht auf eigene Ebenen: jede einzelne lässt das Bild je Takt neu zeichnen. Echtes
 * WebKit (tests/browser/scroll-einst-takt-webkit.mts, ZUHAUSE=1): im Stehen 111 ms CPU je Bild, angehalten
 * 0,6 ms; beim Wischen p90 26 → 17 ms. Angehalten heißt: das Bild bleibt stehen, wie es ist — nichts springt,
 * nach dem Scrollen läuft es an derselben Stelle weiter.
 */
export const SCROLLT_ATTR = "data-scrollt";
let scrolltNachsehen: ReturnType<typeof setTimeout> | null = null;
/** Die Elemente, die gerade das Merkmal tragen: die Eltern der Szenen (nicht `<html>`). */
let markierte: Element[] = [];

/**
 * Schritt C (Chat-Elemente-Plan): Das Merkmal steht nicht mehr am Wurzelelement, sondern am Elternteil jeder Szene
 * (`.zuhause-szene`) — die einzige Regel dazu (`[data-scrollt] > .zuhause-szene *`) wirkt nur dort. Am
 * Wurzelelement zwang jedes Setzen/Entfernen (Wischbeginn, Wischende) eine Stil-Neuberechnung des ganzen
 * Dokuments (1,5 MB Blatt, 66 `:has()`) für diese eine Regel; so betrifft es nur den Teilbaum der Szene.
 * Das Elternteil statt der Szene selbst: wird die Szene mitten im Wisch neu gebaut, trägt das Elternteil das
 * Merkmal weiter. Gesetzt wird nur, was es noch nicht trägt.
 */
function setzeMerkmal(): void {
  for (const szene of document.getElementsByClassName("zuhause-szene")) {
    const eltern = szene.parentElement;
    if (!eltern || markierte.includes(eltern)) continue;
    eltern.setAttribute(SCROLLT_ATTR, "");
    markierte.push(eltern);
  }
}

function loeseMerkmal(): void {
  for (const eltern of markierte) eltern.removeAttribute(SCROLLT_ATTR);
  markierte = [];
}

/** Ob irgendwo gerade das Merkmal steht (für Tests und Messung). */
export function scrolltMerkmalGesetzt(): boolean {
  return markierte.length > 0;
}

function markiereScrollen(): void {
  setzeMerkmal();
  if (scrolltNachsehen !== null) return;
  const pruefe = (): void => {
    if (nutzerScrolltGerade()) {
      scrolltNachsehen = setTimeout(pruefe, NACHSEHEN_MS);
      return;
    }
    scrolltNachsehen = null;
    loeseMerkmal();
  };
  scrolltNachsehen = setTimeout(pruefe, NACHSEHEN_MS);
}

/**
 * Bewegt der Leser gerade diese Fläche (oder, ohne Fläche, irgendeine)? Ein Finger zählt nur, wenn er auf
 * dieser Fläche liegt (`fingerGehoertZu`).
 * Der eine Ort, den jeder fragt, bevor er `scrollTop` schreibt oder Inhalt über dem Leser verändert.
 */
export function nutzerScrolltGerade(el?: Element | null, jetzt = uhr()): boolean {
  starteScrollRuhe();
  return bewegtSich(
    stand,
    jetzt,
    el ? (scrollJeFlaeche.get(el) ?? -Infinity) : undefined,
    fingerGehoertZu(el, fingerZiel),
    el ? (lueckeJeFlaeche.get(el) ?? 0) : undefined,
  );
}

/**
 * Millisekunden seit der letzten Bewegung des Lesers (fremdes Scroll-Ereignis irgendwo oder Rad) — `null`, wenn es
 * noch keine gab. Nur Messung: die Zeile `[tts-weiter]` (tts-queue.ts) sagt damit, ob das System den Ton beim
 * Scrollen anhielt (17.10.2026, Martin: „beim Scrollen unterbricht es, ohne Scrollen läuft es sauber“).
 */
export function msSeitBewegung(jetzt = uhr()): number | null {
  const zuletzt = Math.max(stand.letzterScroll, stand.letztesRad);
  return Number.isFinite(zuletzt) ? Math.max(0, Math.round(jetzt - zuletzt)) : null;
}

/* ------------------------------------------------------------------ *
 * Zähler für die Takt-Zeile ([scroll-t] art=takt … schreiber= aufschub=)
 * ------------------------------------------------------------------ */

interface Zaehlung {
  t: number;
  wer: string;
  art: "schreiben" | "aufschub";
}
const zaehlungen: Zaehlung[] = [];
const ZAEHL_MAX = 200;

function zaehle(wer: string, art: Zaehlung["art"]): void {
  zaehlungen.push({ t: uhr(), wer, art });
  if (zaehlungen.length > ZAEHL_MAX) zaehlungen.splice(0, zaehlungen.length - ZAEHL_MAX);
}

/** `wer:n,wer2:m` (nach Namen), `-` wenn nichts. Reine Rechnung. */
export function zaehlText(liste: readonly Zaehlung[], art: Zaehlung["art"], seit: number): string {
  const n = new Map<string, number>();
  for (const z of liste) if (z.art === art && z.t >= seit) n.set(z.wer, (n.get(z.wer) ?? 0) + 1);
  return n.size ? [...n.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([w, k]) => `${w}:${k}`).join(",") : "-";
}

/**
 * Für die Takt-Zeile eines Wischs: wer hat seit `seit` (performance.now) WÄHREND einer Bewegung geschrieben
 * (`schreiber`, soll `-` sein) — und wer hat gewartet (`aufschub`).
 */
export function schreiberSeit(seit: number): { schreiber: string; aufschub: string } {
  return { schreiber: zaehlText(zaehlungen, "schreiben", seit), aufschub: zaehlText(zaehlungen, "aufschub", seit) };
}

/* ------------------------------------------------------------------ *
 * Schreiben und Warten
 * ------------------------------------------------------------------ */

/**
 * `scrollTop` setzen — nur bei mindestens `MIN_SCHRITT_PX` Unterschied (oder `auchKlein`), mit Marke für die
 * eigene Wache. Wer hier schreibt, während der Leser die Fläche bewegt, wird gezählt (`schreiber=` in der
 * Takt-Zeile): Das darf nur eine ausdrückliche Handlung sein. Gibt zurück, ob geschrieben wurde.
 */
export function schreibeScrollTop(el: Element, wert: number, wer: string, auchKlein = false): boolean {
  starteScrollRuhe();
  const vorher = el.scrollTop;
  if (Math.abs(wert - vorher) < (auchKlein ? 0.5 : MIN_SCHRITT_PX)) return false;
  if (nutzerScrolltGerade(el)) zaehle(wer, "schreiben");
  el.scrollTop = wert;
  eigen.set(el, { oben: el.scrollTop, t: uhr(), wer });
  return true;
}

/**
 * Steht diese Fläche gerade dort, wo ein Programm sie eben hingestellt hat? Dann der Name des Schreibers,
 * sonst `null`. Für die Zeile `art=sprung` der Scroll-Wache: ein Sprung mit `schreiber=-` war der Leser
 * (oder der Browser), keiner aus dem Code, der über `schreibeScrollTop` geht.
 */
export function eigenesSchreibenAn(el: Element, jetzt = uhr()): string | null {
  const m = eigen.get(el);
  return m && istEigenesEcho(m, el.scrollTop, jetzt) ? m.wer : null;
}

interface Auftrag {
  el: Element | null;
  wer: string;
  tun: () => void;
}
const wartend = new Map<unknown, Auftrag>();
let nachsehen: ReturnType<typeof setTimeout> | null = null;

function sieheNach(): void {
  nachsehen = null;
  for (const [schluessel, a] of [...wartend.entries()]) {
    if (nutzerScrolltGerade(a.el)) continue;
    wartend.delete(schluessel);
    try {
      a.tun();
    } catch {
      /* ein Auftrag darf die anderen nicht aufhalten */
    }
  }
  if (wartend.size > 0) nachsehen = setTimeout(sieheNach, NACHSEHEN_MS);
}

/**
 * `tun` jetzt ausführen — oder, solange der Leser `el` bewegt, sobald es still ist. Derselbe `schluessel`
 * wartet nur einmal; der letzte Auftrag gilt. Gibt zurück, ob sofort ausgeführt wurde.
 */
export function nachRuhe(el: Element | null, schluessel: unknown, tun: () => void, wer: string): boolean {
  if (!nutzerScrolltGerade(el)) {
    wartend.delete(schluessel);
    tun();
    return true;
  }
  if (!wartend.has(schluessel)) zaehle(wer, "aufschub");
  wartend.set(schluessel, { el, wer, tun });
  nachsehen ??= setTimeout(sieheNach, NACHSEHEN_MS);
  return false;
}

/** Ein Auftrag, der noch wartet, wird nicht mehr gebraucht. */
export function vergiss(schluessel: unknown): void {
  wartend.delete(schluessel);
}

/** Als Versprechen: erfüllt, sobald der Leser `el` nicht mehr bewegt (sofort, wenn er es nicht tut). */
export function wartAufRuhe(el: Element | null, wer: string): Promise<void> {
  return new Promise((fertig) => {
    nachRuhe(el, {}, fertig, wer);
  });
}

/** Nur für Tests: alles vergessen. */
export function scrollRuheZuruecksetzen(): void {
  Object.assign(stand, leererStand());
  scrollJeFlaeche = new WeakMap();
  lueckeJeFlaeche = new WeakMap();
  eigen = new WeakMap();
  gesehen = new WeakSet();
  fingerZiel = null;
  wartend.clear();
  zaehlungen.length = 0;
  if (nachsehen !== null) clearTimeout(nachsehen);
  nachsehen = null;
  if (scrolltNachsehen !== null) clearTimeout(scrolltNachsehen);
  scrolltNachsehen = null;
  loeseMerkmal();
}


/*
 * Sofort beim Laden einhängen: Wer die Regel erst beim ersten Fragen startet, hat die Berührung davor
 * verpasst (der Finger lag schon, als die Antwort vom Server kam — so im Test der Einstellungen).
 */
starteScrollRuhe();
